import { useCallback, useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { X, Film, Square, Download } from '@/ui/icons'
import {
  buildFlythroughSequence,
  type FlythroughKeyframe,
  type FlythroughWaypoint,
} from '../../utils/waypointFlythroughDriver'

const STYLE_URL = 'mapbox://styles/ayott/cmdldmyxv00eu01ptcd7acb7k'
const TERRAIN_SOURCE_ID = 'mapbox-dem-flythrough'
const MARKER_SOURCE_ID = 'flythrough-marker'
const MARKER_PIN_COLOR = '#5b8fbe'

type PlayState = 'idle' | 'playing' | 'recording' | 'done'

interface Props {
  waypoints: FlythroughWaypoint[]
  tripName?: string
  onClose: () => void
}

export function TripWaypointFlythrough({ waypoints, tripName = 'trip', onClose }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const abortRef = useRef(false)

  const [mapReady, setMapReady] = useState(false)
  const [playState, setPlayState] = useState<PlayState>('idle')
  const [activeFrame, setActiveFrame] = useState(0)

  const keyframes: FlythroughKeyframe[] = buildFlythroughSequence(waypoints)

  // ── Map init ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN

    const firstWp = waypoints[0]
    const center: [number, number] = firstWp
      ? [firstWp.longitude, firstWp.latitude]
      : [0, 20]

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center,
      zoom: 3,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
      preserveDrawingBuffer: true,
    })
    mapRef.current = map

    map.on('load', () => {
      try {
        map.addSource(TERRAIN_SOURCE_ID, {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14,
        })
        map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration: 1.4 })
      } catch (_) {}

      try {
        map.addLayer({
          id: 'sky-flythrough',
          type: 'sky',
          paint: {
            'sky-type': 'atmosphere',
            'sky-atmosphere-sun': [0.0, 0.0],
            'sky-atmosphere-sun-intensity': 15,
          } as mapboxgl.SkyPaint,
        })
      } catch (_) {}

      // Waypoint pin — drawn on the GL canvas so it's captured by MediaRecorder
      map.addSource(MARKER_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
      map.addLayer({
        id: 'flythrough-pin-halo',
        type: 'circle',
        source: MARKER_SOURCE_ID,
        paint: { 'circle-radius': 22, 'circle-color': '#ffffff', 'circle-opacity': 0.22 },
      })
      map.addLayer({
        id: 'flythrough-pin-stroke',
        type: 'circle',
        source: MARKER_SOURCE_ID,
        paint: { 'circle-radius': 11, 'circle-color': '#ffffff', 'circle-opacity': 1 },
      })
      map.addLayer({
        id: 'flythrough-pin-fill',
        type: 'circle',
        source: MARKER_SOURCE_ID,
        paint: { 'circle-radius': 7, 'circle-color': MARKER_PIN_COLOR, 'circle-opacity': 1 },
      })
      try {
        map.addLayer({
          id: 'flythrough-pin-label',
          type: 'symbol',
          source: MARKER_SOURCE_ID,
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
            'text-size': 13,
            'text-offset': [0, 1.8],
            'text-anchor': 'top',
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': 'rgba(0,0,0,0.6)',
            'text-halo-width': 1.5,
          },
        })
      } catch (_) {}

      setMapReady(true)
    })

    return () => {
      abortRef.current = true
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop()
      }
      map.remove()
      mapRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- initialize once

  // ── Waypoint pin — show/hide based on active frame ──────────────────────
  // Waypoint frames are indices 2..(keyframes.length - 2); World=0, Overview=1, Outro=last

  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return
    const src = map.getSource(MARKER_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    if (!src) return

    const isWaypointFrame = activeFrame >= 2 && activeFrame <= keyframes.length - 2
    if (isWaypointFrame) {
      const kf = keyframes[activeFrame]
      src.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: { name: kf.label },
          geometry: { type: 'Point', coordinates: kf.center },
        }],
      })
    } else {
      src.setData({ type: 'FeatureCollection', features: [] })
    }
  }, [activeFrame, mapReady, keyframes])

  // ── Sequential animation ─────────────────────────────────────────────────

  const playSequence = useCallback(
    async (frames: FlythroughKeyframe[], onStep: (i: number) => void) => {
      const map = mapRef.current
      if (!map) return

      abortRef.current = false

      for (let i = 0; i < frames.length; i++) {
        if (abortRef.current) break
        const kf = frames[i]
        onStep(i)

        await new Promise<void>((resolve) => {
          if (i === 0) {
            // First frame: jump instantly, no flyTo animation
            map.jumpTo({ center: kf.center, zoom: kf.zoom, pitch: kf.pitch, bearing: kf.bearing })
            setTimeout(resolve, kf.lingerMs)
          } else {
            map.flyTo({
              center: kf.center,
              zoom: kf.zoom,
              pitch: kf.pitch,
              bearing: kf.bearing,
              duration: kf.flyDurationMs,
              essential: true,
            })
            map.once('moveend', () => {
              if (abortRef.current) { resolve(); return }
              setTimeout(resolve, kf.lingerMs)
            })
          }
        })
      }
    },
    [],
  )

  // ── Play (preview only) ──────────────────────────────────────────────────

  const handlePlay = useCallback(async () => {
    if (!mapReady || playState !== 'idle') return
    setPlayState('playing')
    setActiveFrame(0)

    await playSequence(keyframes, setActiveFrame)

    if (!abortRef.current) setPlayState('done')
  }, [mapReady, playState, keyframes, playSequence])

  // ── Record + play ────────────────────────────────────────────────────────

  const handleRecord = useCallback(async () => {
    const map = mapRef.current
    if (!map || !mapReady || playState !== 'idle') return

    const canvas = map.getCanvas()
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : 'video/webm'

    let stream: MediaStream
    try {
      stream = (canvas as HTMLCanvasElement & { captureStream(fps: number): MediaStream }).captureStream(30)
    } catch {
      return
    }

    chunksRef.current = []
    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: 8_000_000,
    })
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data)
    }
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${tripName.replace(/\s+/g, '-').toLowerCase()}-flythrough.webm`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    }

    recorderRef.current = recorder
    recorder.start(100)

    setPlayState('recording')
    setActiveFrame(0)

    await playSequence(keyframes, setActiveFrame)

    recorder.stop()
    recorderRef.current = null

    if (!abortRef.current) setPlayState('done')
  }, [mapReady, playState, keyframes, tripName, playSequence])

  // ── Stop ─────────────────────────────────────────────────────────────────

  const handleStop = useCallback(() => {
    abortRef.current = true
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop()
      recorderRef.current = null
    }
    setPlayState('idle')
    setActiveFrame(0)
  }, [])

  const handleRestart = useCallback(() => {
    handleStop()
    // Give state a tick to settle, then reset
    setTimeout(() => setPlayState('idle'), 50)
  }, [handleStop])

  const isRunning = playState === 'playing' || playState === 'recording'
  const waypointFrames = keyframes.slice(2, -1) // strip World + 2x Overview

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        aria-label="Close"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Top-right action buttons */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {!isRunning && playState !== 'done' && (
          <>
            <button
              onClick={handlePlay}
              disabled={!mapReady || waypoints.length === 0}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white/90 text-earth-900 text-sm font-semibold shadow hover:bg-white transition-colors disabled:opacity-40"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden><polygon points="5 3 19 12 5 21 5 3" /></svg>
              Preview
            </button>
            <button
              onClick={handleRecord}
              disabled={!mapReady || waypoints.length === 0}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold shadow hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              <Film className="h-3.5 w-3.5" />
              Export .webm
            </button>
          </>
        )}

        {isRunning && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-black/50 text-white text-sm font-semibold">
              {playState === 'recording' && (
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              )}
              {playState === 'recording' ? 'Recording…' : 'Playing…'}
            </div>
            <button
              onClick={handleStop}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors"
            >
              <Square className="h-3.5 w-3.5" />
              Stop
            </button>
          </div>
        )}

        {playState === 'done' && (
          <div className="flex items-center gap-2">
            {playState === 'done' && recorderRef.current === null && chunksRef.current.length > 0 && (
              <span className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-green-600 text-white text-sm font-semibold">
                <Download className="h-3.5 w-3.5" />
                Saved
              </span>
            )}
            <button
              onClick={handleRestart}
              className="px-3.5 py-2 rounded-full bg-white/90 text-earth-900 text-sm font-semibold hover:bg-white transition-colors"
            >
              Restart
            </button>
          </div>
        )}
      </div>

      {/* Portrait canvas container */}
      <div className="relative" style={{ width: 390, height: 720 }}>
        {/* Dotted background to match pampam aesthetic */}
        <div className="absolute inset-0 -m-8 rounded-2xl bg-[#f0ede8]" style={{
          backgroundImage: 'radial-gradient(circle, #c8c4bc 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }} />

        <div
          ref={containerRef}
          className="relative w-full h-full rounded-xl overflow-hidden shadow-2xl"
        />

        {/* Loading overlay */}
        {!mapReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-earth-100 rounded-xl">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {mapReady && waypoints.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-earth-500 text-sm">
            No waypoints with coordinates found.
          </div>
        )}
      </div>

      {/* Bottom keyframe strip */}
      {waypointFrames.length > 0 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[500px] max-w-[95vw]">
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-2xl bg-white/10 backdrop-blur-md overflow-x-auto">
            {/* Overview chip */}
            <div className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              activeFrame <= 1 ? 'bg-primary text-primary-foreground' : 'bg-white/15 text-white'
            }`}>
              Overview
            </div>

            <div className="w-px h-4 bg-white/20 shrink-0" />

            {waypointFrames.map((kf, i) => (
              <div
                key={i}
                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  activeFrame === i + 2 ? 'bg-primary text-primary-foreground' : 'bg-white/15 text-white'
                }`}
              >
                {kf.label}
              </div>
            ))}

            <div className="w-px h-4 bg-white/20 shrink-0" />

            {/* Outro overview chip */}
            <div className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              activeFrame === keyframes.length - 1 ? 'bg-primary text-primary-foreground' : 'bg-white/15 text-white'
            }`}>
              End
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
